import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Check, ArrowRight } from 'lucide-react';
export function HeroSecondaryVisual() {
  const ref = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  const y = useTransform(scrollYProgress, [0, 1], [20, -20]);
  const gauges = [{
    name: 'Experian',
    before: 580,
    after: 720
  }, {
    name: 'Equifax',
    before: 590,
    after: 715
  }, {
    name: 'TransUnion',
    before: 600,
    after: 730
  }];
  const trustChecks = ['No score guarantees', 'No hidden fees', 'Federal compliance', 'All 3 bureaus'];
  return <section ref={ref} className="relative py-12 lg:py-20 overflow-hidden bg-white" data-id="element-194">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-195">
        <motion.div style={{
        y
      }} initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.7
      }} className="relative rounded-3xl overflow-hidden p-8 sm:p-12 lg:p-16" style={{
        background: 'linear-gradient(135deg, #0F2747 0%, #0a3a3a 35%, #0E5C66 65%, #136B47 100%)'
      }} data-id="element-196">
          {/* Decorative blur blobs for depth */}
          <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-emerald-400/20 rounded-full blur-[100px] pointer-events-none" data-id="element-197"></div>
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-emerald-500/15 rounded-full blur-[120px] pointer-events-none" data-id="element-198"></div>
          <div className="absolute top-1/2 right-1/4 w-[300px] h-[300px] bg-gold/10 rounded-full blur-[80px] pointer-events-none" data-id="element-199"></div>

          {/* Subtle grid pattern overlay */}
          <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
          backgroundSize: '24px 24px'
        }} data-id="element-200"></div>

          <div className="relative z-10 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-201">
            {/* LEFT: Copy + CTA */}
            <div className="text-white" data-id="element-202">
              <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[0.95] tracking-tight mb-6" data-id="element-203">
                Your Credit.
                <br data-id="element-204" />
                <span className="text-gold" data-id="element-205">Your Method.</span>
              </motion.h2>

              <motion.p initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="text-lg text-white/95 leading-[1.6] mb-8 max-w-md font-medium" data-id="element-206">
                See exactly how the bureaus report your file — and use the same
                federal framework they're bound by.
              </motion.p>

              {/* Trust checks */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="grid grid-cols-2 gap-3 mb-10 max-w-md" data-id="element-207">
                {trustChecks.map(item => <div key={item} className="flex items-center text-sm text-white/95 font-medium" data-id="element-208">
                    <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 mr-2" data-id="element-209">
                      <Check className="w-3 h-3 text-white" strokeWidth={3} data-id="element-210" />
                    </div>
                    {item}
                  </div>)}
              </motion.div>

              {/* CTA pill + award badge */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.4
            }} className="flex flex-col sm:flex-row items-start sm:items-center gap-5" data-id="element-211">
                <a href="#comparison" className="inline-flex items-center bg-white hover:bg-offwhite text-navy-900 px-8 py-4 rounded-full font-bold text-lg transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5" data-id="element-212">
                  See The Method
                  <ArrowRight className="ml-2 w-5 h-5" data-id="element-213" />
                </a>

                <div className="flex items-center gap-3 bg-navy-900/50 backdrop-blur-sm border border-white/10 rounded-2xl px-4 py-3" data-id="element-214">
                  {/* Laurel decoration */}
                  <svg viewBox="0 0 24 24" className="w-6 h-6 text-gold flex-shrink-0" fill="currentColor" data-id="element-215">
                    <path d="M12 2L9 9H2l5 4-2 7 7-4 7 4-2-7 5-4h-7z" data-id="element-216" />
                  </svg>
                  <div className="leading-tight" data-id="element-217">
                    <div className="text-[10px] text-white/60 uppercase tracking-widest font-bold" data-id="element-218">
                      Editorial
                    </div>
                    <div className="text-sm text-white font-bold" data-id="element-219">
                      Research Platform
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* RIGHT: Phone mockup with bureau scores */}
            <div className="relative flex items-center justify-center min-h-[480px]" data-id="element-220">
              {/* Floating sticker labels */}
              <motion.div initial={{
              opacity: 0,
              scale: 0,
              rotate: 0
            }} whileInView={{
              opacity: 1,
              scale: 1,
              rotate: 8
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.5,
              type: 'spring'
            }} className="absolute top-2 -right-2 lg:right-0 z-20 bg-gold text-navy-900 px-4 py-2 rounded-lg font-bold text-sm shadow-xl" style={{
              rotate: '8deg'
            }} data-id="element-221">
                Bureau Visibility
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              scale: 0
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.6,
              type: 'spring'
            }} className="absolute top-20 -right-4 lg:right-4 z-20 bg-emerald-400 text-navy-900 px-4 py-2 rounded-lg font-bold text-sm shadow-xl" style={{
              rotate: '-5deg'
            }} data-id="element-222">
                Federal Rights
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              scale: 0
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.7,
              type: 'spring'
            }} className="absolute top-40 -right-2 lg:right-2 z-20 bg-cyan-400 text-navy-900 px-4 py-2 rounded-lg font-bold text-sm shadow-xl" style={{
              rotate: '6deg'
            }} data-id="element-223">
                Editorial Authority
              </motion.div>

              {/* Phone mockup frame */}
              <motion.div initial={{
              opacity: 0,
              y: 40
            }} whileInView={{
              opacity: 1,
              y: 0
            }} whileHover={{
              scale: 1.02,
              rotate: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.7,
              delay: 0.3
            }} style={{
              rotate: '-2deg'
            }} className="relative w-full max-w-sm bg-white rounded-[2.5rem] shadow-2xl p-3 z-10" data-id="element-224">
                {/* Phone screen */}
                <div className="bg-white rounded-[2rem] overflow-hidden border border-slate-100" data-id="element-225">
                  {/* Status bar */}
                  <div className="flex items-center justify-between px-6 pt-4 pb-2" data-id="element-226">
                    <span className="text-[10px] font-bold text-navy-900" data-id="element-227">
                      9:41
                    </span>
                    <div className="flex gap-1" data-id="element-228">
                      <div className="w-3 h-2 bg-navy-900 rounded-sm" data-id="element-229"></div>
                      <div className="w-3 h-2 bg-navy-900 rounded-sm" data-id="element-230"></div>
                    </div>
                  </div>

                  {/* Header */}
                  <div className="px-6 py-3 border-b border-slate-100" data-id="element-231">
                    <div className="flex items-center gap-2" data-id="element-232">
                      <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-233"></div>
                      <span className="font-mono text-[10px] font-bold tracking-widest text-navy-900 uppercase" data-id="element-234">
                        Credit Report
                      </span>
                    </div>
                  </div>

                  {/* Score display */}
                  <div className="px-6 py-6 text-center bg-gradient-to-b from-white to-slate-50" data-id="element-235">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2" data-id="element-236">
                      Audit Result
                    </div>
                    <div className="flex items-baseline justify-center gap-1" data-id="element-237">
                      <span className="text-5xl font-extrabold text-emerald-600" data-id="element-238">
                        +130
                      </span>
                      <span className="text-sm font-bold text-slate-500 ml-1" data-id="element-239">
                        pts
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1" data-id="element-240">
                      across 3 bureaus
                    </div>

                    {/* Score bar */}
                    <div className="mt-5 mx-auto max-w-[200px]" data-id="element-241">
                      <div className="h-2 rounded-full bg-slate-100 relative overflow-hidden" data-id="element-242">
                        <motion.div initial={{
                        width: '20%'
                      }} whileInView={{
                        width: '85%'
                      }} viewport={{
                        once: true
                      }} transition={{
                        duration: 1.2,
                        delay: 0.6,
                        ease: 'easeOut'
                      }} className="absolute inset-y-0 left-0 rounded-full" style={{
                        background: 'linear-gradient(90deg, #ef4444 0%, #f59e0b 35%, #84cc16 65%, #1FA971 100%)'
                      }} data-id="element-243"></motion.div>
                        <motion.div initial={{
                        left: '20%'
                      }} whileInView={{
                        left: '85%'
                      }} viewport={{
                        once: true
                      }} transition={{
                        duration: 1.2,
                        delay: 0.6,
                        ease: 'easeOut'
                      }} className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-navy-900 border-2 border-white shadow-md -ml-2" data-id="element-244"></motion.div>
                      </div>
                    </div>
                  </div>

                  {/* Bureau rows */}
                  <div className="px-6 py-4 space-y-3 border-t border-slate-100" data-id="element-245">
                    {gauges.map((gauge, idx) => <motion.div key={gauge.name} initial={{
                    opacity: 0,
                    x: -10
                  }} whileInView={{
                    opacity: 1,
                    x: 0
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 0.4,
                    delay: 0.7 + idx * 0.1
                  }} className="flex items-center justify-between" data-id="element-246">
                        <span className="text-xs font-bold text-slate-700" data-id="element-247">
                          {gauge.name}
                        </span>
                        <div className="flex items-center gap-1.5" data-id="element-248">
                          <span className="text-xs text-slate-400 line-through" data-id="element-249">
                            {gauge.before}
                          </span>
                          <ArrowRight className="w-3 h-3 text-gold" data-id="element-250" />
                          <span className="text-sm font-extrabold text-navy-900" data-id="element-251">
                            {gauge.after}
                          </span>
                          <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded ml-1" data-id="element-252">
                            +{gauge.after - gauge.before}
                          </span>
                        </div>
                      </motion.div>)}
                  </div>

                  {/* Footer disclaimer */}
                  <div className="px-6 py-3 border-t border-slate-100" data-id="element-253">
                    <p className="text-[8px] text-slate-400 italic text-center leading-snug" data-id="element-254">
                      Illustrative scenario. Results vary by individual file.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.6
      }} className="text-center text-xs text-slate-500 mt-6 max-w-2xl mx-auto italic" data-id="element-255">
          Illustrative scenario showing typical bureau reporting
          inconsistencies. Not a guarantee of results. Individual outcomes vary
          based on file history, accuracy of records, and federal dispute
          procedures.
        </motion.p>
      </div>
    </section>;
}